<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-source-branch me-2"></i>{{ $t('page.branches', 'Branches') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-primary" @click="openAdd">
            <i class="mdi mdi-plus me-1"></i>Add Branch
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="setView('all')" :class="{ selected: view==='all' }">
          <div class="hms-stat-num">{{ branches.length }}</div>
          <div class="hms-stat-lbl">Total Branches</div>
        </div>
        <div class="hms-stat" v-for="lvl in [4,5,6]" :key="lvl" @click="filterLevel = (filterLevel == lvl ? '' : String(lvl))" :class="{ selected: filterLevel == lvl }">
          <div class="hms-stat-num" :class="lvlColorClass(lvl)">{{ countByLevel(lvl) }}</div>
          <div class="hms-stat-lbl">Level {{ lvl }}</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-green">{{ totalBeds }}</div>
          <div class="hms-stat-lbl">Beds Across Network</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-info">{{ totalCatchment }}</div>
          <div class="hms-stat-lbl">Catchment Population</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: view==='all' }" @click="view='all'">
          <i class="mdi mdi-view-list me-1"></i>List
        </button>
        <button class="hms-tab" :class="{ active: view==='tree' }" @click="view='tree'">
          <i class="mdi mdi-file-tree me-1"></i>Network Tree
        </button>
      </div>

      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>

      <!-- ============ LIST VIEW ============ -->
      <div v-else-if="view === 'all'" class="hms-card">
        <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <div class="hms-search-wrap">
            <i class="mdi mdi-magnify"></i>
            <input class="hms-search" v-model="search" placeholder="Search name, code, KMFL, county..." />
          </div>
          <select class="hms-input hms-filter" v-model="filterLevel">
            <option value="">All levels</option>
            <option value="1">Level 1 — Community</option>
            <option value="2">Level 2 — Dispensary</option>
            <option value="3">Level 3 — Health Centre</option>
            <option value="4">Level 4 — Sub-County / Primary Hospital</option>
            <option value="5">Level 5 — County Referral</option>
            <option value="6">Level 6 — National Referral</option>
          </select>
          <select class="hms-input hms-filter" v-model="filterStatus">
            <option value="">All status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <span class="hms-muted ms-auto">{{ filteredBranches.length }} branches</span>
        </div>

        <div v-if="filteredBranches.length" style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr>
              <th>Branch</th>
              <th>KEPH Level</th>
              <th>Type</th>
              <th>Parent</th>
              <th>Location</th>
              <th>Beds</th>
              <th>Users</th>
              <th>Status</th>
              <th>Actions</th>
            </tr></thead>
            <tbody>
              <tr v-for="b in filteredBranches" :key="b.branch_id">
                <td>
                  <div class="hms-text fw-semibold">{{ b.branch_name }}</div>
                  <div class="hms-muted-sm hms-mono">{{ b.branch_code }}</div>
                  <div class="hms-muted-sm" v-if="b.kmfl_code">KMFL {{ b.kmfl_code }}</div>
                </td>
                <td>
                  <span class="hms-badge" :class="lvlBadge(b.facility_level)">L{{ b.facility_level }}</span>
                </td>
                <td><span class="hms-text-clip">{{ formatFacilityType(b.facility_type) }}</span></td>
                <td>{{ parentName(b.parent_branch_id) || '—' }}</td>
                <td>
                  <div>{{ b.city || '—' }}</div>
                  <div class="hms-muted-sm" v-if="b.sub_county">{{ b.sub_county }}{{ b.ward ? ' · ' + b.ward : '' }}</div>
                </td>
                <td>
                  <strong>{{ b.bed_capacity || 0 }}</strong>
                  <div class="hms-muted-sm">{{ b.available_beds || 0 }} avail</div>
                </td>
                <td>{{ b.total_users || 0 }}</td>
                <td><span class="hms-badge" :class="statusBadge(b.status)">{{ b.status }}</span></td>
                <td class="d-flex gap-1">
                  <button class="hms-btn hms-btn-sm hms-btn-blue" @click="openDetail(b)" title="View"><i class="mdi mdi-eye"></i></button>
                  <button class="hms-btn hms-btn-sm hms-btn-amber" @click="openEdit(b)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-source-branch"></i>No branches match.</div>
      </div>

      <!-- ============ TREE VIEW ============ -->
      <div v-else-if="view === 'tree'">
        <div v-if="!treeRoots.length" class="hms-card hms-empty">
          <i class="mdi mdi-file-tree"></i>No branches yet.
        </div>
        <div v-for="root in treeRoots" :key="root.branch_id" class="hms-tree-root">
          <branch-node :branch="root" :children-of="childrenOf" :depth="0" @open="openDetail" />
        </div>
      </div>

      <!-- ============ ADD/EDIT MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showForm" @click.self="closeForm">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi" :class="editing ? 'mdi-pencil' : 'mdi-plus-circle'"></i> {{ editing ? 'Edit Branch' : 'Add Branch' }}</span>
            <button class="hms-modal-close" @click="closeForm">&times;</button>
          </div>
          <div v-if="formError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ formError }}</div>

          <div class="hms-section-label">IDENTIFICATION</div>
          <div class="row g-3 mb-3">
            <div class="col-md-7"><label class="hms-label">Branch Name *</label>
              <input class="hms-input" v-model="form.branch_name" placeholder="e.g. Narok Sub-County Hospital" />
            </div>
            <div class="col-md-5"><label class="hms-label">Branch Code *</label>
              <input class="hms-input" v-model="form.branch_code" placeholder="NRK-SCH" />
            </div>
            <div class="col-md-6"><label class="hms-label">Branch Type</label>
              <select class="hms-input" v-model="form.branch_type">
                <option value="main">Main</option>
                <option value="satellite">Satellite</option>
                <option value="clinic">Clinic</option>
                <option value="pharmacy">Pharmacy</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Parent Branch (for referral hierarchy)</label>
              <select class="hms-input" v-model="form.parent_branch_id">
                <option value="">— No parent (top of network) —</option>
                <option v-for="b in availableParents" :key="b.branch_id" :value="b.branch_id">
                  L{{ b.facility_level }} · {{ b.branch_name }}
                </option>
              </select>
            </div>
          </div>

          <div class="hms-section-label">KEPH CLASSIFICATION</div>
          <div class="row g-3 mb-3">
            <div class="col-md-4"><label class="hms-label">Facility Level</label>
              <select class="hms-input" v-model="form.facility_level">
                <option value="1">Level 1 — Community</option>
                <option value="2">Level 2 — Dispensary</option>
                <option value="3">Level 3 — Health Centre</option>
                <option value="4">Level 4 — Sub-County / Primary Hospital</option>
                <option value="5">Level 5 — County Referral</option>
                <option value="6">Level 6 — National Referral</option>
              </select>
            </div>
            <div class="col-md-5"><label class="hms-label">Facility Type</label>
              <select class="hms-input" v-model="form.facility_type">
                <option value="community">Community</option>
                <option value="dispensary">Dispensary</option>
                <option value="health_centre">Health Centre</option>
                <option value="sub_county_hospital">Sub-County Hospital</option>
                <option value="primary_hospital">Primary Hospital</option>
                <option value="county_referral">County Referral</option>
                <option value="national_referral">National Referral</option>
                <option value="specialty">Specialty Hospital</option>
                <option value="medical_clinic">Medical Clinic</option>
              </select>
            </div>
            <div class="col-md-3"><label class="hms-label">KEPH Compliance</label>
              <select class="hms-input" v-model="form.keph_compliance">
                <option value="assessing">Assessing</option>
                <option value="compliant">Compliant</option>
                <option value="partial">Partial</option>
                <option value="non_compliant">Non-Compliant</option>
                <option value="not_applicable">N/A</option>
              </select>
            </div>
          </div>

          <div class="hms-section-label">LOCATION (Kenya MOH structure)</div>
          <div class="row g-3 mb-3">
            <div class="col-md-3"><label class="hms-label">KMFL Code</label>
              <input class="hms-input" v-model="form.kmfl_code" placeholder="12345" />
            </div>
            <div class="col-md-3"><label class="hms-label">County Code</label>
              <input class="hms-input" v-model="form.county_code" placeholder="033" />
            </div>
            <div class="col-md-3"><label class="hms-label">Sub-County</label>
              <input class="hms-input" v-model="form.sub_county" placeholder="Narok East" />
            </div>
            <div class="col-md-3"><label class="hms-label">Ward</label>
              <input class="hms-input" v-model="form.ward" placeholder="Suswa" />
            </div>
            <div class="col-md-6"><label class="hms-label">Country</label>
              <input class="hms-input" v-model="form.country" />
            </div>
            <div class="col-md-6"><label class="hms-label">City / Town</label>
              <input class="hms-input" v-model="form.city" />
            </div>
            <div class="col-12"><label class="hms-label">Address</label>
              <input class="hms-input" v-model="form.address" placeholder="Street / building" />
            </div>
          </div>

          <div class="hms-section-label">CAPACITY</div>
          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Bed Capacity</label>
              <input type="number" class="hms-input" v-model.number="form.bed_capacity" min="0" />
            </div>
            <div class="col-md-6"><label class="hms-label">Catchment Population</label>
              <input type="number" class="hms-input" v-model.number="form.catchment_population" min="0" />
            </div>
          </div>

          <div class="hms-section-label">CONTACT</div>
          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Phone</label>
              <input class="hms-input" v-model="form.phone" />
            </div>
            <div class="col-md-6"><label class="hms-label">Email</label>
              <input type="email" class="hms-input" v-model="form.email" />
            </div>
          </div>

          <div v-if="editing" class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Status</label>
              <select class="hms-input" v-model="form.status">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="saveBranch" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Saving...' : (editing ? 'Save Changes' : 'Create Branch') }}
          </button>
        </div>
      </div>

      <!-- ============ DETAIL MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showDetail" @click.self="showDetail=false">
        <div class="hms-modal" v-if="detail">
          <div class="hms-modal-title">
            <span>
              <i class="mdi mdi-source-branch me-2"></i>{{ detail.branch_name }}
              <span class="hms-badge ms-2" :class="lvlBadge(detail.facility_level)">L{{ detail.facility_level }}</span>
            </span>
            <button class="hms-modal-close" @click="showDetail=false">&times;</button>
          </div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Code</span><span class="hms-mono">{{ detail.branch_code }}</span></div>
            <div><span class="hms-kv-key">Type</span><span>{{ detail.branch_type }} · {{ formatFacilityType(detail.facility_type) }}</span></div>
            <div v-if="detail.parent_name"><span class="hms-kv-key">Parent</span><span>{{ detail.parent_name }}</span></div>
            <div><span class="hms-kv-key">KEPH Compliance</span><span><span class="hms-badge" :class="kephBadge(detail.keph_compliance)">{{ detail.keph_compliance }}</span></span></div>
            <div v-if="detail.kmfl_code"><span class="hms-kv-key">KMFL Code</span><span class="hms-mono">{{ detail.kmfl_code }}</span></div>
            <div><span class="hms-kv-key">Location</span><span>{{ [detail.ward, detail.sub_county, detail.city, detail.country].filter(Boolean).join(' · ') || '—' }}</span></div>
            <div v-if="detail.bed_capacity"><span class="hms-kv-key">Bed Capacity</span><span>{{ detail.bed_capacity }}</span></div>
            <div v-if="detail.catchment_population"><span class="hms-kv-key">Catchment</span><span>{{ formatNumber(detail.catchment_population) }} people</span></div>
            <div><span class="hms-kv-key">Phone</span><span>{{ detail.phone || '—' }}</span></div>
            <div><span class="hms-kv-key">Email</span><span>{{ detail.email || '—' }}</span></div>
            <div><span class="hms-kv-key">Status</span><span><span class="hms-badge" :class="statusBadge(detail.status)">{{ detail.status }}</span></span></div>
          </div>

          <div class="hms-section-label">ACTIVITY</div>
          <div class="hms-activity-grid mb-3">
            <div class="hms-activity-cell"><div class="hms-activity-num">{{ detail.total_users || 0 }}</div><div class="hms-activity-lbl">Users</div></div>
            <div class="hms-activity-cell"><div class="hms-activity-num">{{ detail.total_staff || 0 }}</div><div class="hms-activity-lbl">Staff</div></div>
            <div class="hms-activity-cell"><div class="hms-activity-num">{{ formatNumber(detail.total_patients || 0) }}</div><div class="hms-activity-lbl">Patients</div></div>
            <div class="hms-activity-cell"><div class="hms-activity-num">{{ detail.total_beds || 0 }}</div><div class="hms-activity-lbl">Beds Total</div></div>
            <div class="hms-activity-cell"><div class="hms-activity-num hms-text-success">{{ detail.available_beds || 0 }}</div><div class="hms-activity-lbl">Beds Free</div></div>
            <div class="hms-activity-cell"><div class="hms-activity-num hms-text-info">{{ detail.active_admissions || 0 }}</div><div class="hms-activity-lbl">Active Adm.</div></div>
          </div>

          <div v-if="children.length">
            <div class="hms-section-label">SATELLITE FACILITIES ({{ children.length }})</div>
            <div class="hms-children-list">
              <div v-for="c in children" :key="c.branch_id" class="hms-child">
                <div>
                  <div class="hms-text fw-semibold">{{ c.branch_name }}</div>
                  <div class="hms-muted-sm">{{ c.branch_code }} · {{ formatFacilityType(c.facility_type) }}</div>
                </div>
                <span class="hms-badge" :class="lvlBadge(c.facility_level)">L{{ c.facility_level }}</span>
              </div>
            </div>
          </div>

          <div class="d-flex gap-2 mt-3">
            <button class="hms-btn hms-btn-amber flex-fill" @click="showDetail=false; openEdit(detail)">
              <i class="mdi mdi-pencil me-1"></i>Edit Branch
            </button>
          </div>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;--hms-placeholder:#4a4a4a;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;--hms-placeholder:#aaaaaa;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-text { color: var(--hms-h); }
.hms-text-success { color: var(--hms-success); }
.hms-text-info { color: var(--hms-primary); }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.hms-text-clip { display: inline-block; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: middle; }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 140px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { border-color: var(--hms-primary); transform: translateY(-2px); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-primary); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); margin-top: 4px; }
.hms-text-c-1 { color: #94a3b8; }
.hms-text-c-2 { color: #16a085; }
.hms-text-c-3 { color: #34c38f; }
.hms-text-c-4 { color: #1a7fe8; }
.hms-text-c-5 { color: #9b59b6; }
.hms-text-c-6 { color: var(--hms-danger); }
.hms-stat-green { color: var(--hms-success); }
.hms-stat-info  { color: var(--hms-primary); }

.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; margin-bottom: 16px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 220px; max-width: 340px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-top: 8px; margin-bottom: 8px; }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: top; }
.hms-table tr:hover td { background: var(--hms-primary-lt); }

.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; text-transform: capitalize; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: var(--hms-warning); }
.hms-badge-danger  { background: rgba(220,53,69,.15);  color: var(--hms-danger); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }
.hms-badge-amber   { background: rgba(245,158,11,.15); color: #f59e0b; }
.hms-badge-l1 { background: rgba(148,163,184,.15); color: #94a3b8; }
.hms-badge-l2 { background: rgba(22,160,133,.15); color: #16a085; }
.hms-badge-l3 { background: rgba(52,195,143,.15); color: #34c38f; }
.hms-badge-l4 { background: rgba(26,127,232,.15); color: #1a7fe8; }
.hms-badge-l5 { background: rgba(155,89,182,.15); color: #9b59b6; }
.hms-badge-l6 { background: rgba(220,53,69,.15);  color: var(--hms-danger); }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }

/* Tree view */
.hms-tree-root { margin-bottom: 16px; }

/* Modal */
.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 760px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }
.hms-modal-close:hover { color: var(--hms-h); }

.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; color: var(--hms-h); }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 160px; font-weight: 600; }

.hms-activity-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 8px; }
.hms-activity-cell { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px; text-align: center; }
.hms-activity-num { font-size: 22px; font-weight: 800; color: var(--hms-h); line-height: 1.1; }
.hms-activity-lbl { font-size: 11px; color: var(--hms-s); margin-top: 4px; text-transform: uppercase; letter-spacing: 0.5px; }

.hms-children-list { display: flex; flex-direction: column; gap: 6px; }
.hms-child { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; }

.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }

/* Branch node (tree) */
.hms-bnode { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; margin-bottom: 8px; cursor: pointer; transition: all .15s; }
.hms-bnode:hover { border-color: var(--hms-primary); transform: translateX(2px); }
.hms-bnode-row { display: flex; align-items: center; gap: 12px; }
.hms-bnode-icon { font-size: 24px; color: var(--hms-primary); }
.hms-bnode-body { flex: 1; min-width: 0; }
.hms-bnode-name { color: var(--hms-h); font-weight: 700; font-size: 15px; }
.hms-bnode-sub  { color: var(--hms-m); font-size: 12px; }
.hms-bnode-children { padding-left: 28px; border-left: 2px solid var(--hms-br); margin-left: 16px; margin-top: 6px; }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

// Recursive branch-node component (registered locally)
const BranchNode = {
  name: "BranchNode",
  props: ["branch", "childrenOf", "depth"],
  emits: ["open"],
  computed: {
    kids() { return (this.childrenOf && this.childrenOf[this.branch.branch_id]) || []; }
  },
  methods: {
    formatFacilityType(t) {
      var m = { community:'Community', dispensary:'Dispensary', health_centre:'Health Centre',
        sub_county_hospital:'Sub-County Hospital', primary_hospital:'Primary Hospital',
        county_referral:'County Referral', national_referral:'National Referral',
        specialty:'Specialty', medical_clinic:'Medical Clinic' };
      return m[t] || t;
    },
    levelBadgeClass(lvl) { return 'hms-badge-l' + lvl; }
  },
  template: `
    <div>
      <div class="hms-bnode" @click="$emit('open', branch)">
        <div class="hms-bnode-row">
          <i class="mdi mdi-hospital-building hms-bnode-icon"></i>
          <div class="hms-bnode-body">
            <div class="hms-bnode-name">{{ branch.branch_name }}</div>
            <div class="hms-bnode-sub">{{ branch.branch_code }} · {{ formatFacilityType(branch.facility_type) }}{{ branch.bed_capacity ? ' · ' + branch.bed_capacity + ' beds' : '' }}</div>
          </div>
          <span class="hms-badge" :class="levelBadgeClass(branch.facility_level)">L{{ branch.facility_level }}</span>
        </div>
      </div>
      <div v-if="kids.length" class="hms-bnode-children">
        <branch-node v-for="k in kids" :key="k.branch_id" :branch="k" :children-of="childrenOf" :depth="depth + 1" @open="$emit('open', $event)" />
      </div>
    </div>
  `
};

export default {
  name: "BranchesManagement",
  components: { Navigation, BranchNode },
  data() {
    return {
      loading: true,
      processing: false,
      view: "all",
      search: "",
      filterLevel: "",
      filterStatus: "",

      branches: [],

      // Form
      showForm: false,
      editing: false,
      formError: "",
      form: this.emptyForm(),

      // Detail
      showDetail: false,
      detail: null,
      children: [],

      toastMsg: "", toastError: false, toastTimer: null
    };
  },
  computed: {
    filteredBranches() {
      var list = this.branches;
      if (this.filterLevel)  list = list.filter(b => String(b.facility_level) === String(this.filterLevel));
      if (this.filterStatus) list = list.filter(b => b.status === this.filterStatus);
      var q = this.search.trim().toLowerCase();
      if (q) {
        list = list.filter(b => ((b.branch_name||"")+" "+(b.branch_code||"")+" "+(b.kmfl_code||"")+" "+(b.county_code||"")+" "+(b.city||"")+" "+(b.sub_county||"")).toLowerCase().indexOf(q) >= 0);
      }
      return list;
    },
    availableParents() {
      // Cannot pick yourself or your descendants
      if (!this.editing || !this.form.branch_id) return this.branches;
      var self = this;
      var blocked = new Set([this.form.branch_id]);
      // Walk down children to mark descendants blocked
      var addKids = function(parentId) {
        self.branches.forEach(b => {
          if (b.parent_branch_id == parentId && !blocked.has(b.branch_id)) {
            blocked.add(b.branch_id);
            addKids(b.branch_id);
          }
        });
      };
      addKids(this.form.branch_id);
      return this.branches.filter(b => !blocked.has(b.branch_id));
    },
    treeRoots() {
      return this.branches.filter(b => !b.parent_branch_id);
    },
    childrenOf() {
      var map = {};
      this.branches.forEach(b => {
        if (b.parent_branch_id) {
          if (!map[b.parent_branch_id]) map[b.parent_branch_id] = [];
          map[b.parent_branch_id].push(b);
        }
      });
      return map;
    },
    totalBeds() {
      return this.branches.reduce((s,b) => s + (parseInt(b.bed_capacity) || 0), 0);
    },
    totalCatchment() {
      var n = this.branches.reduce((s,b) => s + (parseInt(b.catchment_population) || 0), 0);
      return this.formatNumber(n);
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
    this.loadBranches();
  },
  beforeUnmount() { if (this.toastTimer) clearTimeout(this.toastTimer); },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    emptyForm() {
      return {
        branch_id: null, branch_name: "", branch_code: "", branch_type: "satellite",
        facility_level: "4", facility_type: "primary_hospital",
        parent_branch_id: "", keph_compliance: "assessing",
        kmfl_code: "", county_code: "", sub_county: "", ward: "",
        bed_capacity: 0, catchment_population: 0,
        address: "", phone: "", email: "", country: "Kenya", city: "",
        status: "active"
      };
    },

    setView(v) { this.view = v; },
    countByLevel(lvl) { return this.branches.filter(b => String(b.facility_level) === String(lvl)).length; },
    parentName(id) {
      if (!id) return null;
      var p = this.branches.find(b => b.branch_id == id);
      return p ? p.branch_name : null;
    },
    formatFacilityType(t) {
      var m = { community:'Community', dispensary:'Dispensary', health_centre:'Health Centre',
        sub_county_hospital:'Sub-County Hospital', primary_hospital:'Primary Hospital',
        county_referral:'County Referral', national_referral:'National Referral',
        specialty:'Specialty', medical_clinic:'Medical Clinic' };
      return m[t] || t;
    },
    formatNumber(n) {
      if (n === null || n === undefined || n === "") return "0";
      return Number(n).toLocaleString();
    },
    lvlBadge(lvl) { return 'hms-badge-l' + lvl; },
    lvlColorClass(lvl) { return 'hms-text-c-' + lvl; },
    statusBadge(s) { return ({ active:'hms-badge-success', inactive:'hms-badge-neutral' })[s] || 'hms-badge-neutral'; },
    kephBadge(k) { return ({ compliant:'hms-badge-success', partial:'hms-badge-warning', non_compliant:'hms-badge-danger', assessing:'hms-badge-info', not_applicable:'hms-badge-neutral' })[k] || 'hms-badge-neutral'; },

    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2400);
    },

    async loadBranches() {
      this.loading = true;
      try {
        var r = await executeJson({ function: "getBranches" }, hms);
        if (r.data && r.data.success) this.branches = (r.data.data && r.data.data.Branches) || [];
      } catch(e) { console.error(e); }
      this.loading = false;
    },

    openAdd() {
      this.editing = false;
      this.form = this.emptyForm();
      this.formError = "";
      this.showForm = true;
    },
    openEdit(b) {
      this.editing = true;
      this.form = Object.assign(this.emptyForm(), {
        branch_id:           b.branch_id,
        branch_name:         b.branch_name || "",
        branch_code:         b.branch_code || "",
        branch_type:         b.branch_type || "satellite",
        facility_level:      String(b.facility_level || "4"),
        facility_type:       b.facility_type || "primary_hospital",
        parent_branch_id:    b.parent_branch_id || "",
        keph_compliance:     b.keph_compliance || "assessing",
        kmfl_code:           b.kmfl_code || "",
        county_code:         b.county_code || "",
        sub_county:          b.sub_county || "",
        ward:                b.ward || "",
        bed_capacity:        Number(b.bed_capacity) || 0,
        catchment_population:Number(b.catchment_population) || 0,
        address:             b.address || "",
        phone:               b.phone || "",
        email:               b.email || "",
        country:             b.country || "Kenya",
        city:                b.city || "",
        status:              b.status || "active"
      });
      this.formError = "";
      this.showForm = true;
    },
    closeForm() { this.showForm = false; },
    async saveBranch() {
      this.formError = "";
      if (!this.form.branch_name || !this.form.branch_code) { this.formError = "Branch name and code are required."; return; }
      this.processing = true;
      try {
        var fn = this.editing ? "updateBranch" : "createBranch";
        var r = await executeJson(Object.assign({ function: fn }, this.form), hms);
        if (r.data && r.data.success) {
          this.showToast(this.editing ? "Branch updated" : "Branch created");
          this.showForm = false;
          await this.loadBranches();
        } else { this.formError = r.data.message || "Failed."; }
      } catch(e) { this.formError = "Connection error."; }
      this.processing = false;
    },

    async openDetail(b) {
      this.showDetail = true;
      this.detail = b;
      this.children = [];
      try {
        var r = await executeJson({ function: "getBranchDetail", branch_id: b.branch_id }, hms);
        if (r.data && r.data.success) {
          this.detail = r.data.data.Branch || b;
          this.children = r.data.data.Children || [];
        }
      } catch(e) { /* keep summary view */ }
    }
  }
};
</script>
